import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  ogType?: string;
  ogUrl?: string;
  robots?: string;
  jsonLd?: object | object[];
}

export const SEOConfig = {
  siteName: 'Lenzura',
  siteUrl: 'https://lenzura.com',
  default: {
    title: 'Lenzura - Professional Photography & Videography Services',
    description: 'Find top-tier freelance photographers and videographers on Lenzura.',
    keywords: 'photography, videography, freelance, creative services',
    ogImage: '/og-image.png',
  },
};

const toAbsolute = (url?: string) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : SEOConfig.siteUrl;
  return url.startsWith('/') ? `${origin}${url}` : `${origin}/${url}`;
};

const normalizeJsonLd = (jsonLd?: object | object[]) =>
  jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

export const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  ogType = 'website',
  ogUrl,
  robots,
  jsonLd,
}) => {
  const meta = {
    title: title || SEOConfig.default.title,
    description: description || SEOConfig.default.description,
    keywords: keywords || SEOConfig.default.keywords,
    ogImage: toAbsolute(ogImage || SEOConfig.default.ogImage),
  };
  const url = toAbsolute(canonical || ogUrl) || (typeof window !== 'undefined' ? window.location.href : undefined);
  const ldBlocks = normalizeJsonLd(jsonLd);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      {robots && <meta name="robots" content={robots} />}

      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={meta.ogImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SEOConfig.siteName} />
      <meta property="og:locale" content="en_US" />
      {url && <meta property="og:url" content={url} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.ogImage} />

      {canonical && <link rel="canonical" href={toAbsolute(canonical)} />}

      {ldBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
};

export const GlobalSEO: React.FC = () => (
  <Helmet>
    <meta charset="UTF-8" />
    <meta name="msvalidate.01" content="FC5285F44EB9E1B8A282535301C3BE38" />
    <script async src="https://www.googletagmanager.com/gtag/js?id=G-EK97BZL5B4" />
    <script>{`
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-EK97BZL5B4');
    `}</script>
    <script type="application/ld+json">{JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SEOConfig.siteName,
      url: SEOConfig.siteUrl,
      logo: `${SEOConfig.siteUrl}/og-image.png`,
      sameAs: [
        'https://twitter.com/lenzura',
        'https://instagram.com/lenzura',
        'https://linkedin.com/company/lenzura',
      ],
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SEOConfig.siteName,
      url: SEOConfig.siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SEOConfig.siteUrl}/gigs?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    })}</script>
  </Helmet>
);

export const NoIndex: React.FC<{ content?: string }> = ({ content = 'noindex, follow' }) => (
  <Helmet>
    <meta name="robots" content={content} />
  </Helmet>
);